import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import { ExternalLink, Link as LinkIcon } from 'lucide-react'

import {
    DataTableColumn,
    DataTableAction,
    FilterToggle,
} from '@/components/ui/data-table-customized'
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Tender } from '@/modules/Tenders/types'
import { generatePathDatacentral, paths } from '@/utils/path'
import FollowButton from './FollowButton/indext'

export type TenderListVariant = 'new' | 'following' | 'unfollowed'

// --- CellSelect (moved from FollowingTenders/column.tsx) ---

const CellSelect = ({
    value,
    onChange,
}: {
    value: boolean | null
    onChange: (value: boolean) => void
}) => {
    const { t } = useTranslation()
    const selectedValue =
        value === null ? '-' : value === true ? 'true' : 'false'
    return (
        <div className="w-full">
            <Select
                value={selectedValue}
                onValueChange={(v) => onChange(v === 'true')}
            >
                <SelectTrigger>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent>
                    <SelectGroup>
                        <SelectItem value="true">{t('yes')}</SelectItem>
                        <SelectItem value="false">{t('no')}</SelectItem>
                    </SelectGroup>
                </SelectContent>
            </Select>
        </div>
    )
}

// --- Constants (moved from AllTenders/columns.tsx) ---

export const FIELD_TYPES = {
    HH: 'Hàng hóa',
    XL: 'Xây lắp',
    PTV: 'Phi tư vấn',
    TV: 'Tư vấn',
    HON_HOP: 'Hỗn hợp',
} as const

export const BID_FORM_TYPES = {
    CGTT: 'Chào giá trực tuyến',
    CGTTRG: 'Chào giá trực tuyến theo quy trình rút gọn',
    DTRR: 'Đấu thầu rộng rãi',
    DH_GNV: 'Đặt hàng, giao nhiệm vụ',
    ESHOP: 'Mua sắm trực tuyến',
    DH: 'Đặt hàng',
    GNV: 'Giao nhiệm vụ',
    CHCT: 'Chào hàng cạnh tranh',
    CHCTRG: 'Chào hàng cạnh tranh rút gọn',
    DTHC: 'Đấu thầu hạn chế',
    MSTT: 'Mua sắm trực tiếp',
    CDT: 'Chỉ định thầu',
    CDTRG: 'Chỉ định thầu rút gọn',
    TTH: 'Tự thực hiện',
    LCNT_DB: 'Lựa chọn nhà thầu trong trường hợp đặc biệt',
    TVCN: 'Tư vấn cá nhân',
    TCTVCN: 'Tuyển chọn tư vấn cá nhân',
    DPCT: 'Đàm phán cạnh tranh',
    QCBS: 'Tuyển chọn trên cơ sở Chất lượng và Chi phí (QCBS)',
    DPG: 'Đàm phán giá',
    QBS: 'Tuyển chọn tư vấn dựa trên cơ sở chất lượng (QBS)',
    FBS: 'Lựa chọn theo mức ngân sách cố định (FBS),',
    LCS: 'Tuyển chọn tư vấn có chi phí thấp nhất (LCS)',
    CQS: 'Tuyển chọn dựa trên năng lực (CQS)',
    SSS: 'Tuyển chọn từ một nguồn duy nhất (SSS)',
    TGTC: 'Tuyển chọn tư vấn trong các khoản vay cho các thể chế hoặc tổ chức trung gian tài chính',
    NHBD: 'Tuyển chọn tư vấn trong các khoản vay được Ngân hàng bảo đảm',
    TVCT: 'Tuyển chọn một số loại hình tư vấn cụ thể',
    TGTHCD: 'Tham gia thực hiện cộng đồng',
    TVCNRG: 'Tư vấn cá nhân rút gọn',
    KHAC: 'Khác',
} as const

// --- Column options ---

interface TenderColumnOptions {
    onPotentialChange?: (
        tender_id: number,
        is_potential: boolean,
        not_potential_reason: string | null
    ) => void
}

// --- Columns ---

export const getTenderListColumns = (
    t: TFunction,
    variant: TenderListVariant,
    options?: TenderColumnOptions
): DataTableColumn<Tender>[] => {
    const columns: DataTableColumn<Tender>[] = []

    if (variant === 'following') {
        columns.push(
            {
                key: 'is_potential',
                title: t('is_potential'),
                width: 120,
                render: (_value, row) => (
                    <CellSelect
                        value={row.is_potential}
                        onChange={(value) => {
                            options?.onPotentialChange?.(
                                row.id,
                                value,
                                row.not_potential_reason
                            )
                        }}
                    />
                ),
            },
            {
                key: 'not_potential_reason',
                title: t('not_potential_reason'),
                width: 250,
                cellClassName: 'whitespace-normal',
                render: (_value, row) => (
                    <span
                        className="text-sm max-w-full"
                        title={row.not_potential_reason || undefined}
                    >
                        {!row.is_potential && row.not_potential_reason}
                    </span>
                ),
            }
        )
    }

    columns.push(
        {
            key: 'followers',
            title: t('followers_count'),
            width: 80,
            render: (_value, row) => (
                <FollowButton
                    tender_id={row.id}
                    is_followed={row.is_followed}
                />
            ),
        },
        {
            key: 'code',
            title: t('code'),
            width: 150,
            pinned: 'left',
            render: (_value, row) => (
                <Link
                    to={generatePathDatacentral(paths.tenderDetail, {
                        tenderId: row.id.toString(),
                    })}
                    className="text-blue-600 hover:underline"
                >
                    {row.code}
                </Link>
            ),
        },
        {
            key: 'name',
            title: t('name'),
            width: 500,
            render: (value) => (
                <span className="text-sm truncate block" title={value}>
                    {value}
                </span>
            ),
        },
        {
            key: 'investor',
            title: t('investor'),
            width: 300,
            render: (value) => (
                <span className="text-sm truncate block" title={value}>
                    {value}
                </span>
            ),
        },
        {
            key: 'location',
            title: t('location'),
            width: 200,
        },
        {
            key: 'publish_date',
            title: t('publish_date'),
            width: 160,
            render: (value) =>
                value ? dayjs(value).format('HH:mm DD/MM/YYYY') : '',
        },
        {
            key: 'bid_time',
            title: t('bid_time'),
            width: 180,
            render: (value) =>
                value ? dayjs(value).format('HH:mm DD/MM/YYYY') : '',
        },
        {
            key: 'bid_form',
            title: t('bid_form'),
            width: 200,
            render: (value) => (
                <span className="text-sm truncate block">
                    {BID_FORM_TYPES[value as keyof typeof BID_FORM_TYPES] ||
                        value}
                </span>
            ),
        }
    )

    return columns
}

// --- Actions ---

interface TenderActionOptions {
    onViewDetails: (row: Tender) => void
    onViewBidResult?: (row: Tender) => void
}

export const getTenderListActions = (
    t: TFunction,
    options: TenderActionOptions
): DataTableAction<Tender>[] => {
    const actions: DataTableAction<Tender>[] = [
        {
            key: 'view-details',
            label: t('view_details'),
            icon: <ExternalLink className="w-4 h-4" />,
            onClick: (row) => options.onViewDetails(row),
            disabled: (row) => !row.detail_url,
        },
    ]

    if (options.onViewBidResult) {
        actions.push({
            key: 'view-bid-result',
            label: t('view_bid_result'),
            icon: <LinkIcon className="w-4 h-4" />,
            onClick: (row) => options.onViewBidResult!(row),
            disabled: (row) => !row.id,
        })
    }

    return actions
}

// --- Filter Toggles ---

export const getTenderFilterToggles = (
    t: TFunction,
    variant: TenderListVariant
): FilterToggle[] => {
    const toggles: FilterToggle[] = []

    if (variant === 'following') {
        toggles.push(
            { key: 'is_potential', label: t('is_potential') },
            { key: 'not_potential_reason', label: t('not_potential_reason') }
        )
    }

    toggles.push(
        { key: 'followers', label: t('followers_count') },
        { key: 'code', label: t('code') },
        { key: 'name', label: t('name') },
        { key: 'investor', label: t('investor') },
        { key: 'location', label: t('location') },
        { key: 'publish_date', label: t('publish_date') },
        { key: 'bid_time', label: t('bid_time') },
        { key: 'bid_form', label: t('bid_form') }
    )

    return toggles
}
